import { type AllCoverablesOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import CatBreedStep from './steps/cat/CatBreedStep';
import CatGenderStep from './steps/cat/CatGenderStep';
import CatNameStep from './steps/cat/CatNameStep';
import DogBreedStep from './steps/dog/DogBreedStep';
import DogGenderStep from './steps/dog/DogGenderStep';
import DogNameStep from './steps/dog/DogNameStep';
import WhoIsTheInsuranceForStep from './steps/AdultAccident/WhoIsTheInsuranceForStep';
import NameOfTheInsured from './steps/AdultAccident/NameOfTheInsured';
import EmploymentStep from './steps/AdultAccident/EmploymentStep';
import ContentAddressStep from './steps/privateContent/ContentAddressStep';
import type { ProductStatus } from '../../recoil/types/onsaTypes';
import PetBirthdateStep from './steps/dog/PetBirthdateStep';
import ContentNumberInHouseholdStep from './steps/privateContent/ContentNumberInHouseholdStep';
import YoungContentDobStep from './steps/youngContent/YoungContentDobStep';
import ContentAlreadyCoveredStep from './steps/privateContent/ContentAlreadyCoveredStep';
import RegistrationNumberStep from './steps/Car/RegistrationNumberStep';
import NameOfChild from './steps/childAccident/NameOfChild';
import TravelInsuranceTypeStep from './steps/travel/TravelInsuranceTypeStep';
import HowShouldChildBeCovered from './steps/childAccident/HowShouldChildBeCovered';
import BoughtOrLeasedStep from './steps/Car/BoughtOrLeasedStep';
import NoRegistrationNumberStep from './steps/Car/NoRegistrationNumberStep';
import LeasingPeriodStep from './steps/Car/LeasingPeriodStep';
import ContentAgeOfOldestInHouseholdStep from './steps/privateContent/ContentAgeOfOldestInHouseholdStep';
import PetHealthyFor6MonthsStep from './steps/dog/PetHealthyFor6MonthsStep';
import HouseAddressStep from './steps/privateHouse/HouseAddressStep';
import HouseAgeOfOldestInHouseholdStep from './steps/privateHouse/HouseAgeOfOldestInHouseholdStep';
import HouseBBRStep from './steps/privateHouse/HouseBBRStep';
import type {
    PrhHolidayHomeOnsaDTO,
    PrhHouseOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/house';
import HouseBuyingAHouseStep from './steps/privateHouse/HouseBuyingAHouseStep';
import { COVERABLE_CODE } from '../../constants/coverable';
import IsStationaryStep from './steps/uvp/caravan/IsStationaryStep';
import HouseUWIQuestionsStep from './steps/privateHouse/HouseUWIStep/HouseUWIQuestionsStep';
import { shouldRenderUWIQuestionsStep } from './steps/privateHouse/HouseUWIStep/HouseUWIQuestionsUtil';
import CurrentValueOfUvpStep from './steps/uvp/CurrentValueOfUvpStep';
import AgeOfOldestPersonInHouseholdOfUvpStep from './steps/uvp/AgeOfOldestPersonInHouseholdOfUvpStep';
import {
    CaravanRegOrNoRegNumberStep,
    TrailerRegOrNoRegNumberStep
} from './steps/uvp/UvpRegOrNoRegNumberStep';
import { getAgeOfTheOldestFromCoverableList } from '../../utils/coverablesUtil';
interface CoverableInfoConfig {
    title: string;
    steps: Array<{
        id: string;
        disabled?: boolean;
        Component: React.ComponentType<{
            goNext: () => void;
            goPrevious: () => void;
            wizardNextStep: () => void;
        }>;
    }>;
}

export const getCoverableInformationConfig = (
    currentProduct: ProductStatus,
    coverables: AllCoverablesOnsaDTO
): CoverableInfoConfig => {
    switch (currentProduct?.flavourName) {
        case 'Hundeforsikring':
            return getDogCoverableInfoConfig;
        case 'Katteforsikring':
            return getCatCoverableInfoConfig;
        case 'Ulykkesforsikring':
            return getAdultAccidentCoverableInfoConfig(coverables, currentProduct);
        case 'Ulykkesforsikring til unge':
            return getYoungAccidentCoverableInfoConfig(coverables, currentProduct);
        case 'Børneforsikring':
            return getchildAccidentCoverableInfoConfig;
        case 'Indboforsikring':
            return getPrivateContentCoverableInfoConfig(coverables);
        case 'Indboforsikring til unge':
            return getYoungContentCoverableInfoConfig;
        case 'Campingvognsforsikring':
            return getCaravanCoverableInfoConfig(coverables);
        case 'Påhængsvognsforsikring':
            return getTrailerCoverableInfoConfig(coverables);
        case 'Bilforsikring':
            return getMotorCoverableInfoConfig(coverables, currentProduct);
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
            return getTravelCoverableInfoConfig;
        case 'Husforsikring':
            return getHouseCoverableInfoConfig(coverables, currentProduct);
        case 'Fritidshusforsikring':
            return getHouseCoverableInfoConfig(coverables, currentProduct);
        default:
            return {
                title: '',
                steps: []
            };
    }
};

const getHouseCoverableInfoConfig = (
    coverables: AllCoverablesOnsaDTO,
    currentProduct: ProductStatus
): CoverableInfoConfig => {
    let coverable: PrhHouseOnsaDTO | PrhHolidayHomeOnsaDTO | undefined;
    if (currentProduct.flavourCode === COVERABLE_CODE.prhHouse) {
        coverable = coverables.houses?.find(
            (element) => element.fixedId === currentProduct.fixedId
        );
    } else {
        coverable = coverables.holidayHomes?.find(
            (element) => element.fixedId === currentProduct.fixedId
        );
    }
    const existingAgeOfTheOldest = getAgeOfTheOldestFromCoverableList(coverables);
    const houseCoverableInfoConfig: CoverableInfoConfig = {
        title:
            currentProduct.flavourCode === COVERABLE_CODE.prhHouse
                ? 'Husforsikring'
                : 'Fritidshusforsikring',
        steps: [
            {
                id: 'HouseAddressStep',
                Component: HouseAddressStep
            },
            {
                id: 'HouseAgeOfOldestStep',
                Component: HouseAgeOfOldestInHouseholdStep,
                disabled: !!existingAgeOfTheOldest
            },
            {
                id: 'HouseBBRStep',
                Component: HouseBBRStep
            },
            {
                id: 'HouseBuyingAHouseStep',
                Component: HouseBuyingAHouseStep
            },
            {
                id: 'HouseUWIQuestionsStep',
                Component: HouseUWIQuestionsStep,
                disabled: !shouldRenderUWIQuestionsStep(coverable)
            }
        ]
    };
    return houseCoverableInfoConfig;
};

const getCaravanCoverableInfoConfig = (coverables: AllCoverablesOnsaDTO): CoverableInfoConfig => {
    const existingAgeOfTheOldest = getAgeOfTheOldestFromCoverableList(coverables);
    const caravanCoverableInfoConfig: CoverableInfoConfig = {
        title: 'Campingvognsforsikring',
        steps: [
            {
                id: 'IsStationaryStep',
                Component: IsStationaryStep
            },
            {
                id: 'CaravanRegOrNoRegNumberStep',
                Component: CaravanRegOrNoRegNumberStep
            },
            {
                id: 'CurrentValueOfUvpStep',
                Component: CurrentValueOfUvpStep
            },
            {
                id: 'AgeOfOldestPersonInHouseholdOfUvpStep',
                Component: AgeOfOldestPersonInHouseholdOfUvpStep,
                disabled: !!existingAgeOfTheOldest
            }
        ]
    };

    return caravanCoverableInfoConfig;
};

const getTrailerCoverableInfoConfig = (coverables: AllCoverablesOnsaDTO): CoverableInfoConfig => {
    const existingAgeOfTheOldest = getAgeOfTheOldestFromCoverableList(coverables);
    const trailerCoverableInfoConfig: CoverableInfoConfig = {
        title: 'Påhængsvognsforsikring',
        steps: [
            {
                id: 'TrailerRegOrNoRegNumberStep',
                Component: TrailerRegOrNoRegNumberStep
            },
            {
                id: 'CurrentValueOfUvpStep',
                Component: CurrentValueOfUvpStep
            },
            {
                id: 'AgeOfOldestPersonInHouseholdOfUvpStep',
                Component: AgeOfOldestPersonInHouseholdOfUvpStep,
                disabled: !!existingAgeOfTheOldest
            }
        ]
    };
    return trailerCoverableInfoConfig;
};

const getMotorCoverableInfoConfig = (
    coverables: AllCoverablesOnsaDTO,
    currentProduct: ProductStatus
): CoverableInfoConfig => {
    const coverable = coverables.cars?.find(
        (element) => element.fixedId === currentProduct.fixedId
    );
    const carCoverableInfoConfig: CoverableInfoConfig = {
        title: 'Bilforsikring',
        steps: [
            {
                id: 'RegistrationNumberStep',
                Component: RegistrationNumberStep
            },
            {
                id: 'NoRegistrationNumberStep',
                Component: NoRegistrationNumberStep,
                disabled: coverable?.name !== undefined
            },
            {
                id: 'BoughtOrLeasedStep',
                Component: BoughtOrLeasedStep
            },
            {
                id: 'LeasingPeriod',
                Component: LeasingPeriodStep,
                disabled: !coverable?.isInLeasing
            }
        ]
    };

    return carCoverableInfoConfig;
};

const getDogCoverableInfoConfig: CoverableInfoConfig = {
    title: 'Hundeforsikring',
    steps: [
        {
            id: 'DogNameStep',
            Component: DogNameStep
        },
        {
            id: 'DogBreedStep',
            Component: DogBreedStep
        },
        {
            id: 'DogGenderStep',
            Component: DogGenderStep
        },
        {
            id: 'DogBirthdateStep',
            Component: PetBirthdateStep
        },
        {
            id: 'PetHealthyFor6MonthsStep',
            Component: PetHealthyFor6MonthsStep
        }
    ]
};

const getCatCoverableInfoConfig: CoverableInfoConfig = {
    title: 'Katteforsikring',
    steps: [
        {
            id: 'CatNameStep',
            Component: CatNameStep
        },
        {
            id: 'CatBreedStep',
            Component: CatBreedStep
        },
        {
            id: 'CatGenderStep',
            Component: CatGenderStep
        },
        {
            id: 'CatBirthdateStep',
            Component: PetBirthdateStep
        },
        {
            id: 'PetHealthyFor6MonthsStep',
            Component: PetHealthyFor6MonthsStep
        }
    ]
};

const getAdultAccidentCoverableInfoConfig = (
    coverables: AllCoverablesOnsaDTO,
    currentProduct: ProductStatus
): CoverableInfoConfig => {
    const coverable = coverables.adultAccidents?.find(
        (element) => element.fixedId === currentProduct.fixedId
    );
    const adultAccidentCoverableInfoConfig: CoverableInfoConfig = {
        title: 'Ulykkesforsikring',
        steps: [
            {
                id: 'AdultAccWhoIsTheInsuranceFor',
                Component: WhoIsTheInsuranceForStep
            },
            {
                id: 'AdultAccNameOfTheInsured',
                Component: NameOfTheInsured,
                disabled: !coverable?.insuranceForOtherPerson
            },
            {
                id: 'AdultAccEmployment',
                Component: EmploymentStep
            }
        ]
    };

    return adultAccidentCoverableInfoConfig;
};

const getYoungAccidentCoverableInfoConfig = (
    coverables: AllCoverablesOnsaDTO,
    currentProduct: ProductStatus
): CoverableInfoConfig => {
    const coverable = coverables.youngAccidents?.find(
        (element) => element.fixedId === currentProduct.fixedId
    );

    const youngAccidentCoverableInfoConfig: CoverableInfoConfig = {
        title: 'Ulykkesforsikring til unge',
        steps: [
            {
                id: 'YoungAccWhoIsTheInsuranceFor',
                Component: WhoIsTheInsuranceForStep
            },
            {
                id: 'YoungAccNameOfTheInsured',
                Component: NameOfTheInsured,
                disabled: !coverable?.insuranceForOtherPerson
            }
        ]
    };
    return youngAccidentCoverableInfoConfig;
};

const getchildAccidentCoverableInfoConfig: CoverableInfoConfig = {
    title: 'Ulykkesforsikring til børn',
    steps: [
        {
            id: 'NameOfChild',
            Component: NameOfChild
        },
        {
            id: 'HowShouldChildBeCovered',
            Component: HowShouldChildBeCovered
        }
    ]
};

const getPrivateContentCoverableInfoConfig = (
    coverables: AllCoverablesOnsaDTO
): CoverableInfoConfig => {
    const existingAgeOfTheOldest = getAgeOfTheOldestFromCoverableList(coverables);
    return {
        title: 'Indboforsikring',
        steps: [
            {
                id: 'ContentAddressStep',
                Component: ContentAddressStep
            },
            {
                id: 'ContentNumberInHouseholdStep',
                Component: ContentNumberInHouseholdStep
            },
            {
                id: 'ContentAgeOfOldestInHouseholdStep',
                Component: ContentAgeOfOldestInHouseholdStep,
                disabled: !!existingAgeOfTheOldest
            },
            {
                id: 'ContentAlreadyCoveredStep',
                Component: ContentAlreadyCoveredStep
            }
        ]
    };
};

const getYoungContentCoverableInfoConfig: CoverableInfoConfig = {
    title: 'Indboforsikring til unge',
    steps: [
        {
            id: 'ContentAddressStep',
            Component: ContentAddressStep
        },
        {
            id: 'YoungContentDobStep',
            Component: YoungContentDobStep
        },
        {
            id: 'ContentAlreadyCoveredStep',
            Component: ContentAlreadyCoveredStep
        }
    ]
};

const getTravelCoverableInfoConfig: CoverableInfoConfig = {
    title: 'Rejseforsikring',
    steps: [
        {
            id: 'TravelInsuranceTypeStep',
            Component: TravelInsuranceTypeStep
        }
    ]
};
